import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  ChevronDown,
  Download,
  FileClock,
  FileDown,
  FileSpreadsheet,
  History,
  Loader2,
  RefreshCw,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { RefreshButton } from "@/components/data/refresh-button";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTable, type Column } from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
import { SearchInput } from "@/components/data/search-input";
import {
  DEREGISTRATION_TONE,
  RegistrationStatusBadge,
  TONE_CLASSES,
  toneDotClass,
} from "@/components/data/status-badge";
import { DateRangeFilter, dayToIso } from "@/components/data/date-range-filter";
import { ExportsDock } from "./-components/exports-dock";
import { LayoutUploadsDock } from "./-components/layout-uploads-dock";
import {
  bajasApi,
  downloadBajaTemplate,
  DEREGISTRATION_STATUSES,
  type Deregistration,
  type DeregistrationStatus,
} from "@/api/deregistrations";
import { queryKeys } from "@/api/query-keys";
import { useCursorList } from "@/hooks/use-cursor-list";
import { authErrorMessage } from "@/auth/errors";
import { requireRole } from "@/auth/role-guard";
import { formatRelative } from "@/lib/format";
import { UploadLayoutDialog } from "./-dialogs/upload-layout-dialog";
import {
  RugLoginGuardBanner,
  useRugLoginValid,
} from "@/components/rug/rug-login-guard";

// Los 9 estados salen de `@/api/deregistrations` (origen único). Antes el filtro tenía su
// propia lista y le faltaban `scheduled`, `signature_attempted` y `signed_unverified`, o
// sea los tres estados que introdujo la firma automática: no se podían filtrar.
const STATUS_OPTIONS = DEREGISTRATION_STATUSES;

const searchSchema = z.object({
  status: z.string().optional(),
  q: z.string().optional(),
  // `YYYY-MM-DD` en la URL (legible y compartible); a ISO al llamar a la API.
  from: z.string().optional(),
  to: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/bajas/")({
  validateSearch: searchSchema,
  beforeLoad: requireRole(["admin", "baja"]),
  component: BajasPage,
});

function BajasPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const navigateTo = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(search.q ?? "");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  const [uploadsHistoryOpen, setUploadsHistoryOpen] = useState(false);
  const { isValid: loginValid } = useRugLoginValid("baja");

  // Multi-estado: en la URL viaja como CSV ("queued,failed"). Vacío = todos.
  // Se descartan los valores desconocidos (link viejo, estado renombrado, valor pegado a
  // mano): si no, el trigger del filtro pintaría la clave cruda de i18n en pantalla y
  // ningún chip quedaría resaltado mientras el botón dice "1 seleccionados".
  const selectedStatuses = (
    search.status ? search.status.split(",").filter(Boolean) : []
  ).filter((s): s is DeregistrationStatus =>
    (STATUS_OPTIONS as readonly string[]).includes(s),
  );
  const statusFilter = selectedStatuses.length ? selectedStatuses.join(",") : undefined;
  // Rango en ISO. Sin rango, el backend cae al año en curso (tanto el listado como los
  // conteos), así que la pantalla arranca coherente consigo misma.
  const rango = { from: dayToIso(search.from, "start"), to: dayToIso(search.to, "end") };

  // Listado paginado por cursor — se usa cuando no hay búsqueda activa
  const listParams = { status: statusFilter, ...rango, limit: 25 };
  const listQuery = useCursorList({
    queryKey: queryKeys.bajas.list(listParams),
    fetcher: bajasApi.list,
    params: listParams,
    enabled: !searchTerm,
    // El estado de una baja lo mueve el bot, así que al entrar siempre se revalida
    // (el staleTime global de 30s serviría datos viejos al volver a la pantalla).
    refetchOnMount: "always",
  });

  // Contadores del MISMO rango que la tabla (no reaccionan a la búsqueda). Clic en un chip
  // filtra. Van acotados a propósito: globales dirían "48" sobre una tabla vacía en cuanto
  // el rango no fuera el de las bajas, y eso se lee como pérdida de datos.
  // Si el endpoint falla, la fila simplemente no se pinta: la tabla y el resto de los
  // filtros siguen usables.
  // `staleTime` propio: son 9 queries COUNT que recorren el GSI completo, y la tabla es
  // PAY_PER_REQUEST. Con el staleTime global de 30 s cada montaje de la pantalla y cada
  // `invalidateQueries(['bajas'])` del reintento relee el índice entero.
  const countsQuery = useQuery({
    queryKey: queryKeys.bajas.counts(rango),
    queryFn: () => bajasApi.counts(rango),
    staleTime: 5 * 60_000,
  });
  const counts = countsQuery.data;

  // Búsqueda — endpoint dedicado, sin paginación. Texto libre sobre contrato, folio y
  // VIN. Antes el frontend adivinaba con `/^\d{4,}$/`: "si parece número de 4+ dígitos
  // es folio, si no es contrato". Un VIN caía siempre en la rama de contrato y devolvía
  // cero sin decir por qué. Los estados sí viajan: el backend ya los respeta, así que
  // los chips siguen vivos durante la búsqueda.
  const searchParams = { q: searchTerm.trim(), status: statusFilter };
  const searchQuery = useQuery({
    queryKey: queryKeys.bajas.search(searchParams),
    queryFn: () => bajasApi.search(searchParams),
    enabled: !!searchTerm,
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => bajasApi.retry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bajas.all });
      toast.success(t("bajas.retryToast"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  // Exportación asíncrona: se pide con el filtro que se ve en pantalla y el archivo lo
  // arma un worker. Al crearla se abre el cajón, que es donde aparecerá cuando esté lista.
  const exportMutation = useMutation({
    mutationFn: bajasApi.createExport,
    onSuccess: () => {
      setExportsOpen(true);
      qc.invalidateQueries({ queryKey: queryKeys.bajas.exports() });
      toast.success(t("bajas.exports.started"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const templateMutation = useMutation({
    mutationFn: downloadBajaTemplate,
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const onSearchChange = (value: string) => {
    setSearchTerm(value);
    navigate({
      search: (prev) => ({ ...prev, q: value || undefined }),
      replace: true,
    });
  };

  // El siguiente valor se calcula DENTRO del updater, a partir de `prev`, no desde
  // `selectedStatuses`. `navigate` es asíncrono: dos clics rápidos leerían el mismo estado
  // viejo y el segundo pisaría al primero, dejando un solo estado seleccionado.
  const toggleStatus = (value: DeregistrationStatus) => {
    navigate({
      search: (prev) => {
        const actuales = prev.status ? prev.status.split(",").filter(Boolean) : [];
        const next = actuales.includes(value)
          ? actuales.filter((s) => s !== value)
          : [...actuales, value];
        return { ...prev, status: next.length ? next.join(",") : undefined };
      },
      replace: true,
    });
  };
  const clearStatuses = () => {
    navigate({
      search: (prev) => ({ ...prev, status: undefined }),
      replace: true,
    });
  };

  const columns: Column<Deregistration>[] = [
    {
      id: "contract",
      header: t("bajas.columns.contract"),
      cell: (r) => (
        <div>
          {/* El contrato es opcional; sin respaldo la celda se veía vacía. El VIN
              tiene su propia columna, así que aquí basta la raya. */}
          <p className="font-medium">{r.contractNumber || "—"}</p>
          {r.folioRug ? (
            <p className="text-xs text-muted-foreground">
              {t("bajas.columns.folio", { folio: r.folioRug })}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      id: "serial",
      header: t("bajas.columns.serial"),
      cell: (r) => (
        <span className="text-sm font-mono">{r.assetSerialNumber}</span>
      ),
    },
    {
      id: "creditor",
      header: t("bajas.columns.creditor"),
      cell: (r) => (
        <p className="text-sm">
          {r.principalCreditor ?? r.cancellationSnapshot?.acreedorNombre ?? "—"}
        </p>
      ),
    },
    {
      id: "status",
      header: t("bajas.columns.status"),
      cell: (r) => <RegistrationStatusBadge status={r.status} />,
    },
    {
      id: "attempts",
      header: t("bajas.columns.attempts"),
      cell: (r) => <span className="text-sm tabular-nums">{r.attempts}</span>,
      headClassName: "text-right",
      className: "text-right",
    },
    {
      id: "updated",
      header: t("bajas.columns.updated"),
      cell: (r) => (
        <span className="text-sm text-muted-foreground">
          {formatRelative(r.updatedAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (r) =>
        r.status === "failed" ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={retryMutation.isPending || !loginValid}
            onClick={(e) => {
              e.stopPropagation();
              retryMutation.mutate(r.id);
            }}
          >
            <RefreshCw className="size-3.5" />
            {t("bajas.actions.retry")}
          </Button>
        ) : null,
      headClassName: "w-32",
      className: "text-right",
    },
  ];

  const isSearching = !!searchTerm;
  const data = isSearching ? (searchQuery.data ?? []) : listQuery.items;
  const isLoading = isSearching ? searchQuery.isLoading : listQuery.isLoading;
  const isFetching = isSearching ? searchQuery.isFetching : listQuery.isFetching;
  const error = isSearching ? searchQuery.error : listQuery.error;

  return (
    <>
      <PageHeader
        title={t("bajas.title")}
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton
              onClick={() => {
                listQuery.refetch();
                searchQuery.refetch();
                countsQuery.refetch();
              }}
              spinning={
                listQuery.isFetching || searchQuery.isFetching || countsQuery.isFetching
              }
            />
            <Button
              variant="outline"
              disabled={exportMutation.isPending}
              onClick={() =>
                exportMutation.mutate({
                  filter: { q: searchTerm || undefined, statuses: selectedStatuses, ...rango },
                })
              }
            >
              {exportMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <FileDown className="size-4" />
              )}
              {t("bajas.exports.action")}
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label={t("bajas.exports.title")}
              onClick={() => setExportsOpen(true)}
            >
              <History className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label={t("bajas.layoutUploads.open")}
              title={t("bajas.layoutUploads.open")}
              onClick={() => setUploadsHistoryOpen(true)}
            >
              <FileClock className="size-4" />
            </Button>
            <Button
              variant="outline"
              disabled={templateMutation.isPending}
              onClick={() => templateMutation.mutate()}
            >
              {templateMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Download className="size-4" />
              )}
              {t("bajas.upload.downloadTemplate")}
            </Button>
            <Button onClick={() => setUploadOpen(true)}>
              <Upload className="size-4" />
              {t("bajas.actions.uploadLayout")}
            </Button>
          </div>
        }
      />

      <div className="mb-4">
        <RugLoginGuardBanner flow="baja" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <SearchInput
          value={searchTerm}
          onChange={onSearchChange}
          placeholder={t("bajas.search.placeholder")}
          className="sm:max-w-md flex-1"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="sm:w-48 justify-between font-normal">
              <span className="truncate">
                {selectedStatuses.length === 0
                  ? t("bajas.filters.status.all")
                  : selectedStatuses.length === 1
                    ? t(`bajas.filters.status.${selectedStatuses[0]}`)
                    : t("bajas.filters.statusCount", {
                        count: selectedStatuses.length,
                      })}
              </span>
              <ChevronDown className="size-4 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuCheckboxItem
              checked={selectedStatuses.length === 0}
              onCheckedChange={clearStatuses}
              onSelect={(e) => e.preventDefault()}
            >
              {t("bajas.filters.status.all")}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            {STATUS_OPTIONS.map((value) => (
              <DropdownMenuCheckboxItem
                key={value}
                checked={selectedStatuses.includes(value)}
                onCheckedChange={() => toggleStatus(value)}
                onSelect={(e) => e.preventDefault()}
              >
                {t(`bajas.filters.status.${value}`)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <DateRangeFilter
          from={search.from}
          to={search.to}
          onChange={(r) =>
            navigate({
              search: (prev) => ({ ...prev, from: r.from, to: r.to }),
              replace: true,
            })
          }
          disabled={isSearching}
        />
      </div>

      {/* Los chips siguen vivos durante la búsqueda: `/deregistrations/search` ya acepta
          estados. Antes se atenuaban porque no los aceptaba y un chip clicable habría
          hecho MENTIR al filtro. Con match exacto daba igual (0 o 1 fila), pero la
          búsqueda por fragmento devuelve conjuntos y ahí filtrar por estado sí sirve.
          El año sigue deshabilitado: la búsqueda es all-time, igual que en inscripciones.
          Los conteos son del año y no reaccionan al término, así que durante la búsqueda
          informan del universo, no del resultado. */}
      {counts ? (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            type="button"
            onClick={clearStatuses}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
              selectedStatuses.length === 0
                ? "border-primary bg-primary/10 font-medium text-foreground"
                : "border-input text-muted-foreground hover:bg-muted"
            }`}
          >
            {t("bajas.counts.total")}
            <span className="rounded-full bg-muted px-1.5 text-xs font-semibold text-foreground">
              {counts.total}
            </span>
          </button>
          {STATUS_OPTIONS.filter((s) => (counts.byStatus[s] ?? 0) > 0).map((s) => {
            const active = selectedStatuses.includes(s);
            const tone = DEREGISTRATION_TONE[s];
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-all ${
                  TONE_CLASSES[tone]
                } ${
                  active
                    ? "font-medium ring-2 ring-current ring-offset-1"
                    : "hover:brightness-95"
                }`}
              >
                <span className={`size-1.5 rounded-full ${toneDotClass(tone)}`} />
                {t(`bajas.filters.status.${s}`)}
                <span className="rounded-full bg-background/80 px-1.5 text-xs font-semibold text-foreground">
                  {counts.byStatus[s]}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <DataTable<Deregistration>
        data={data}
        columns={columns}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRowClick={(r) =>
          navigateTo({
            to: "/bajas/$bajaId",
            params: { bajaId: r.id },
          })
        }
        loadMore={
          !isSearching && listQuery.hasNextPage
            ? {
                hasMore: true,
                isLoading: listQuery.isFetchingNextPage,
                onLoadMore: () => listQuery.fetchNextPage(),
              }
            : undefined
        }
        emptyState={
          <EmptyState
            icon={FileSpreadsheet}
            title={
              isSearching
                ? t("bajas.empty.noResultsTitle")
                : selectedStatuses.length
                  ? t("bajas.empty.noStatusTitle")
                  : t("bajas.empty.noneTitle")
            }
            description={
              isSearching
                ? t("bajas.empty.noResultsDescription", { term: searchTerm })
                : selectedStatuses.length
                  ? t("bajas.empty.noStatusDescription")
                  : t("bajas.empty.noneDescription")
            }
            actions={
              !isSearching ? (
                <Button onClick={() => setUploadOpen(true)}>
                  <Upload className="size-4" />
                  {t("bajas.actions.uploadLayout")}
                </Button>
              ) : null
            }
          />
        }
      />

      <ExportsDock open={exportsOpen} onOpenChange={setExportsOpen} />
      <LayoutUploadsDock open={uploadsHistoryOpen} onOpenChange={setUploadsHistoryOpen} />
      <UploadLayoutDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </>
  );
}
